import { colorFields, eyebrowField } from "./_shared";
/**
 * Countdown — a live timer counting down to a target date/time (launch,
 * sale end, drop). Five layout variants. Once the target passes, the section
 * shows the seller's "expired" message instead of the digits.
 */
export const countdownSchema = {
    type: "countdown",
    name: { ar: "عداد تنازلي", en: "Countdown" },
    description: {
        ar: "عداد تنازلي لإطلاق منتج أو عرض لفترة محدودة.",
        en: "A live timer counting down to a launch or sale.",
    },
    maxPerTemplate: 2,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: { ar: "طريقة عرض العداد.", en: "How the timer is laid out." },
            options: [
                { value: "count-blocks", label: { ar: "مربعات", en: "Blocks" } },
                { value: "count-inline", label: { ar: "مضمّن", en: "Inline" } },
                { value: "count-circles", label: { ar: "دوائر", en: "Circles" } },
                { value: "count-minimal", label: { ar: "بسيط", en: "Minimal" } },
                { value: "count-banner", label: { ar: "شريط", en: "Banner" } },
                // Ported from EasyOrders `promo-countdown-hero` (elite family): a
                // full-bleed promo photograph with the copy + CTA on the leading side
                // and the timer on the trailing side. A variant rather than a new
                // section — same content model (title, subhead, target date) plus a
                // background image and a CTA.
                { value: "count-hero", label: { ar: "بانر مصوّر", en: "Image hero" } },
                // Ported from EasyOrders `hs-timer-offer` (blast family, 2026-07-20):
                // a CONTAINED rounded card, title on the leading edge, the digits in
                // small white chips through the middle, and a pill CTA on the trailing
                // edge — one 104px line at 768 and up, stacking to 178px below it.
                //
                // A variant rather than a section: the content model is the
                // countdown's own field for field (title, target date, CTA label and
                // href, expired message). The measurement is what separates it from
                // `count-banner`, which is a full-bleed centred accent BAND with no
                // CTA at all and no card: theirs is 104px against `count-banner`'s
                // ~200px, and the three-part row is its whole identity.
                { value: "count-strip", label: { ar: "كارت مستعرض", en: "Offer strip" } },
                // Ported from EasyOrders `fsc-flash-sale` (prime family, 2026-07-20):
                // a contained rounded card reading headline · BARE digits · a line of
                // supporting copy · a pill button carrying a discount code.
                //
                // A variant rather than a type, and the near-neighbour is `count-strip`
                // rather than `count-banner` — so the separation had to be measured
                // between two things that are both "a contained card with a timer and
                // a button". They are different in three ways that all show up in the
                // box:
                //
                //  - the DIGITS. `count-strip` prints them in raised chips at 14px;
                //    this one prints them bare at `clamp(32px, 10vw, 40px)` w700 with
                //    `letter-spacing: -.02em`, and the timer is the loudest thing in
                //    the card rather than its quietest.
                //  - the COLUMN COUNT. `count-strip` is a three-part row (title,
                //    chips, CTA); this is a FOUR-part row — headline, timer, a
                //    `max-width: 260px` description, then the button.
                //  - the BOX. MEASURED on prime-books: 140px at 1200/1440 against
                //    `count-strip`'s 104, and it stacks to a 242px centred column at
                //    768 and below where `count-strip` stacks to 178.
                //
                // Its button is also the second block in the vocabulary whose CTA is
                // an ACTION rather than a destination (after `search-prompt`): it
                // copies a discount code to the clipboard. That is carried by the
                // `couponCode` field below rather than by a sentinel href.
                { value: "count-flash", label: { ar: "كارت فلاش", en: "Flash sale card" } },
            ],
            default: "count-blocks",
        },
        ...eyebrowField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 80 },
            default: { ar: "قريبًا", en: "Launching soon" },
        },
        {
            id: "subhead",
            type: "textarea",
            i18n: true,
            label: { ar: "وصف (اختياري)", en: "Subhead (optional)" },
            validate: { max: 200 },
        },
        {
            id: "targetDate",
            type: "datetime",
            label: { ar: "الموعد المستهدف", en: "Target date & time" },
            helpText: {
                ar: "العداد بيعد تنازلي لحد الموعد ده.",
                en: "The timer counts down to this moment.",
            },
        },
        {
            id: "expiredMessage",
            type: "text",
            i18n: true,
            label: { ar: "رسالة بعد الانتهاء", en: "Message when finished" },
            validate: { max: 120 },
            default: { ar: "إحنا أونلاين دلوقتي!", en: "We're live!" },
        },
        // ---- count-hero only ----------------------------------------------------
        // Their section is driven by `--pch-*` custom properties on the element:
        // a desktop + a mobile background image, an overlay alpha, a min-height
        // and a CTA pair. Everything below is scoped to the variant so the other
        // five layouts do not grow controls they never paint.
        {
            id: "image",
            type: "image",
            label: { ar: "صورة الخلفية", en: "Background image" },
            helpText: {
                ar: "الصورة اللي ورا النص. بتتقص لملء العرض.",
                en: "The photograph behind the copy. Cropped to fill.",
            },
            focalPoint: true,
            showIf: { variant: ["count-hero", "count-strip"] },
        },
        {
            id: "overlayOpacity",
            type: "range",
            label: { ar: "تعتيم الصورة", en: "Image dimming" },
            helpText: {
                ar: "زوّده لو النص مش واضح على الصورة.",
                en: "Raise this if the copy is hard to read over the photo.",
            },
            min: 0,
            max: 90,
            step: 1,
            default: 48,
            showIf: { variant: ["count-hero", "count-strip"] },
        },
        {
            id: "height",
            type: "select",
            label: { ar: "الارتفاع", en: "Height" },
            options: [
                { value: "compact", label: { ar: "قصير", en: "Compact" } },
                { value: "comfortable", label: { ar: "متوسط", en: "Comfortable" } },
                { value: "tall", label: { ar: "طويل", en: "Tall" } },
            ],
            default: "comfortable",
            // NOT offered on `count-strip`: their card is a fixed 104px line at 768
            // and up and 178px stacked below it, with no height knob of any kind.
            showIf: { variant: ["count-hero"] },
        },
        {
            /**
             * The CARD's ground — `count-strip` only.
             *
             * Deliberately NOT the shared `surface` field, which `colorFields()`
             * already contributes below: that one paints the SECTION band, and this
             * block's section band is the page. The coloured thing is the contained
             * card sitting on it, which is a second, nested ground and needs its own
             * slot. Same five slot names so there is one vocabulary for "which band",
             * not two.
             *
             * Their block authors a literal `--to-bg: #ffd600` yellow with a navy
             * pill. Ported as a band rather than the hex, for the reason every other
             * port records: a hardcoded ground ignores the store's palette the
             * moment a seller changes it. `accent` is the default because that is
             * what their instance reads as — a saturated panel on a white page.
             */
            id: "cardSurface",
            type: "select",
            label: { ar: "لون الكارت", en: "Card background" },
            options: [
                { value: "page", label: { ar: "زي الصفحة", en: "Same as page" } },
                { value: "alt", label: { ar: "فاتح مختلف", en: "Subtle" } },
                { value: "primary", label: { ar: "اللون الأساسي", en: "Primary" } },
                { value: "accent", label: { ar: "اللون المميّز", en: "Accent" } },
                { value: "ink", label: { ar: "غامق", en: "Dark" } },
            ],
            default: "accent",
            showIf: { variant: ["count-strip", "count-flash"] },
        },
        {
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الزر (اختياري)", en: "Button label (optional)" },
            validate: { max: 40 },
            // On `count-flash` this is the LEAD-IN printed before the code — their
            // button reads "Use Code: FLASH30", which is a label plus a value, not
            // one string. The renderer falls back to "Use code" when it is blank.
            showIf: { variant: ["count-hero", "count-strip", "count-flash"] },
        },
        {
            id: "ctaHref",
            type: "url",
            label: { ar: "رابط الزر", en: "Button destination" },
            showIf: { variant: ["count-hero", "count-strip"] },
        },
        {
            /**
             * `count-flash` only — the discount code its button carries.
             *
             * This is the ONE field the flash card needs that the countdown's
             * existing model could not express, and it is why the button is an
             * action rather than a link: their `[data-fsc-code-btn]` copies the code
             * to the clipboard and flips to a confirmation, it does not navigate.
             * Reusing `ctaHref` with a sentinel value was rejected for the same
             * reason `search-prompt` rejected it — a sentinel href leaks a special
             * case into every variant that reads the field.
             *
             * Leaving it empty is meaningful: the card then prints `ctaLabel` as a
             * plain link button if an href is set, and nothing at all if neither is.
             */
            id: "couponCode",
            type: "text",
            label: { ar: "كود الخصم", en: "Discount code" },
            helpText: {
                ar: "الزرار بينسخ الكود ده. سيبه فاضي لو مفيش كود.",
                en: "The button copies this code. Leave empty if there is no code.",
            },
            validate: { max: 24 },
            showIf: { variant: ["count-flash"] },
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=countdown.js.map